import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import { useChartPeriod } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import {
  CurrencyValue,
  Flex,
  LineChart,
  formatNumber
} from '@blockanalitica/ui';
import { useMemo, useState } from 'react';

/**
 * Cleanup and deduplicate entries in order to "merge" two data sources
 **/
function cleanupData(data) {
  const map = new Map();

  for (const item of data) {
    const balance = Number(item.balance); // converts "0E-18" → 0

    if (balance > 0) {
      // only keep if positive
      map.set(item.date, item);
    }
  }

  // return sorted by date (optional but nice)
  return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
}

export default function HistoricChart({ symbol }) {
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [, updateChartConfigWithPeriod] = useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();
  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  const [oldData, _oldError] = useApi({
    url: `/tokens/0xdc035d45d973e3ec169d2276ddab16f1e407384f/holders/0x7d4958454a3f520bda8be764d06591b054b0bf33/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  const [newData, newError] = useApi({
    url: `/tokens/0xdc035d45d973e3ec169d2276ddab16f1e407384f/holders/0x1e1D42781FC170EF9da004Fb735f56F0276d01B8/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (newError) {
    throw newError;
  }

  const data = cleanupData([...(oldData ?? []), ...(newData ?? [])]);

  const datasets = useMemo(() => {
    return [
      {
        xField: 'date',
        yField: 'balance'
      }
    ];
  }, []);

  const yScaleValueFormatter = (value) => {
    return formatNumber(Number(value));
  };

  const valueFormatter = useMemo(() => {
    function formatValue(value: string) {
      return (
        <CurrencyValue
          value={Number(value)}
          options={{
            currency: tokensMapping(symbol),
            iconSize: '3'
          }}
        />
      );
    }
    return formatValue;
  }, [symbol]);

  if (data.length > 0) {
    return (
      <Flex flexDirection="column" gap="xs">
        <LineChart
          key="balance"
          data={data}
          valueFormatter={valueFormatter}
          datasets={datasets}
          yScaleValueFormatter={yScaleValueFormatter}
          timeFilter={timeFilter}
          config={config}
        />
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
